import { Checkbox } from './Checkbox.js'
import { Stack, RowBetween } from '../layout/Stack.js'
import { Button } from './Button.js'
import { Text } from './Typography.js'

export interface CheckboxOption {
  value: string
  label: React.ReactNode
  description?: React.ReactNode
  /** Cannot be turned off here, and says why when hovered. */
  locked?: boolean
}

export interface CheckboxGroupProps {
  options: CheckboxOption[]
  value: string[]
  onChange: (value: string[]) => void
  /** Offers "choose all" / "choose none" above the list. */
  selectAll?: boolean
  disabled?: boolean
}

/**
 * Several of a known set, each needing a sentence.
 *
 * A role is *"a named set an administrator defines"* out of nine permissions,
 * and each one has to say what it lets somebody do — `read_records` and
 * `break_glass` are one word apart and open very different things. Nine separate
 * checkboxes laid out by a screen is nine chances to forget the description.
 *
 * `selectAll` is a convenience with teeth: a role with everything ticked is an
 * administrator, and somebody should have to mean it rather than reach it by
 * dragging.
 */
export function CheckboxGroup({
  options,
  value,
  onChange,
  selectAll,
  disabled,
}: CheckboxGroupProps) {
  const unlocked = options.filter((o) => !o.locked).map((o) => o.value)
  const allOn = unlocked.every((v) => value.includes(v))

  const toggle = (v: string, on: boolean) =>
    onChange(on ? [...new Set([...value, v])] : value.filter((x) => x !== v))

  return (
    <Stack gap={4}>
      {selectAll && (
        <RowBetween>
          <Text variant="caption">
            {value.length} of {options.length} chosen
          </Text>
          <Button
            variant="tertiary"
            size="sm"
            flush
            disabled={disabled}
            onClick={() =>
              onChange(
                allOn
                  ? value.filter((v) => !unlocked.includes(v))
                  : [...new Set([...value, ...unlocked])],
              )
            }
          >
            {allOn ? 'Choose none' : 'Choose all'}
          </Button>
        </RowBetween>
      )}
      <Stack gap={3}>
        {options.map((o) => (
          <Checkbox
            key={o.value}
            checked={value.includes(o.value)}
            disabled={disabled || o.locked}
            label={o.label}
            description={o.description}
            onCheckedChange={(on) => toggle(o.value, on === true)}
          />
        ))}
      </Stack>
    </Stack>
  )
}
